import { BulbOutlined } from '@ant-design/icons'
import { Pto } from '@outcoreaix/pto'
import { useGetChangelogUnreadCountQuery } from 'api/analytics-api'
import { AppRoutes } from 'app/app-routes'
import { authStorage } from 'app/auth-storage'
import { useWorkspace } from 'hooks/useWorkspace'
import { Badge, Button, Tooltip } from 'antd'
import { FC, ReactElement, useMemo } from 'react'
import { useNavigate } from 'react-router-dom'

const sidebarButtonClass = 'w-full flex !h-10 justify-normal mr-auto hover:!bg-secondaryWithOpacity font-semibold'

type Props = {
  collapsed?: boolean
  onNavigate?: () => void
}

export const WhatsNewToolbarButton: FC<Props> = ({ collapsed = false, onNavigate }) => {
  const navigate = useNavigate()
  const { currentWorkspaceId, currentWorkspace } = useWorkspace()
  const user = authStorage.user

  const showWhatsNew = useMemo(
    () =>
      !!currentWorkspaceId &&
      !!user &&
      !user.roles.includes(Pto.Users.UserRole.SystemAdmin) &&
      (currentWorkspace?.currentMember?.role === Pto.Workspaces.WorkspaceRole.Staff ||
        currentWorkspace?.currentMember?.role === Pto.Workspaces.WorkspaceRole.Admin),
    [currentWorkspace?.currentMember?.role, currentWorkspaceId, user?.id, user?.roles]
  )

  const { data: unread } = useGetChangelogUnreadCountQuery(
    { workspaceId: currentWorkspaceId ?? -1 },
    {
      skip: !showWhatsNew || currentWorkspaceId == null
    }
  )

  const unreadCount = unread?.newEntryCount ?? 0

  const go = () => {
    if (!currentWorkspaceId) return
    navigate(`${AppRoutes.workspaces}/${currentWorkspaceId}${AppRoutes.whatsNew}`)
    onNavigate?.()
  }

  if (!showWhatsNew) return null

  const icon = <BulbOutlined className="[&>svg]:w-[23px] [&>svg]:h-[23px] text-[var(--ant-color-text)]" />

  const wrapWithBadge = (node: ReactElement, offset: [number, number]) =>
    unreadCount > 0 ? (
      <Badge count={unreadCount} overflowCount={99} size="small" offset={offset}>
        {node}
      </Badge>
    ) : (
      node
    )

  if (collapsed) {
    return (
      <Tooltip title="What's New" placement="right">
        <div className="flex w-full justify-center pt-3">
          {wrapWithBadge(
            <Button
              type="text"
              icon={icon}
              className="flex h-10 w-10 shrink-0 items-center justify-center hover:!bg-secondaryWithOpacity min-w-10"
              onClick={go}
              aria-label="What's New"
            />,
            [-4, 4]
          )}
        </div>
      </Tooltip>
    )
  }

  /**
   * Expanded, the badge hangs off the bulb itself, not off the right edge of the full-width button.
   * Collapsed it is anchored to the 40x40 button box (icon is 23px, centred), so `[4, -4]` here puts
   * the count in the same spot relative to the icon as `[-4, 4]` does there.
   */
  return (
    <Button
      type="text"
      icon={wrapWithBadge(icon, [4, -4])}
      className={`${sidebarButtonClass} !w-full max-w-full`}
      onClick={go}
    >
      What&apos;s New
    </Button>
  )
}
